import { useMemo, useState } from "react"
import { UserPlus, MoreHorizontal, Users, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from "lucide-react"
import { cn } from "@/utils/Functions"
import { useIsDemo } from "@/hooks/common/useIsDemo"
import { useOrg } from "@/contexts/OrgContext"
import { useDemoOrg } from "@/contexts/DemoOrgContext"
import { useProfile } from "@/hooks/profile/useProfile"
import { useOrgMembers, useOrgInvites, useOrgRoles } from "@/hooks/organization"
import { useDemoOrgData } from "@/hooks/organization/useDemoOrgData"
import { DEF_JAM_ORG_ID } from "@/mockData/common/demoFilters"
import { notifyDemoAction } from "@/utils/demoNotifications"
import { Button } from "@/components/ui/Button"
import { Avatar } from "@/components/ui/Avatar"
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectPopup,
  SelectItem,
} from "@/components/ui/Select"
import {
  Menu,
  MenuTrigger,
  MenuPopup,
  MenuItem,
} from "@/components/ui/menu"
import { InviteMembersModal, EditMemberModal } from "@/components/organization"
import { SettingsSectionHeader } from "../components/SettingsSectionHeader"

const DEMO_CURRENT_USER_ID = "user-1"

const STANDARD_ROLES: { value: string; label: string }[] = [
  { value: "primary_owner", label: "Owner" },
  { value: "admin", label: "Admin" },
  { value: "member", label: "Member" },
  { value: "viewer", label: "Viewer" },
]

const ROLE_LABELS: Record<string, string> = {
  primary_owner: "Owner",
  admin: "Admin",
  billing_admin: "Billing Admin",
  member: "Member",
  viewer: "Viewer",
}

const ROWS_PER_PAGE_OPTIONS = ["10", "25", "50"]

type Tab = "members" | "invitations"

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length === 0 || !parts[0]) return "?"
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function roleDisplay(member: any): string {
  if (member.role === "custom" && member.customRoleName) return member.customRoleName
  return ROLE_LABELS[member.role] ?? member.role ?? "Member"
}

function MembersSection() {
  const isDemo = useIsDemo()
  const { selectedOrgId } = useOrg()
  const demoOrgContext = useDemoOrg()
  const { profile } = useProfile()

  const orgId = isDemo
    ? demoOrgContext?.selectedOrg?.orgId ?? DEF_JAM_ORG_ID
    : selectedOrgId

  // ── Data ────────────────────────────────────────────────────────────
  const {
    members: apiMembers,
    updateMember,
    removeMember,
  } = useOrgMembers(isDemo ? undefined : orgId ?? undefined)
  const { invites: apiInvites, createInvite, cancelInvite, resendInvite } =
    useOrgInvites(isDemo ? undefined : orgId ?? undefined)
  const { customRoles } = useOrgRoles(isDemo ? undefined : orgId ?? undefined)

  const { getMembersByOrgId, getInvitationsByOrgId, getCustomRolesByOrgId } =
    useDemoOrgData()

  const members: any[] = isDemo ? getMembersByOrgId(orgId ?? DEF_JAM_ORG_ID) : apiMembers
  const invites: any[] = isDemo
    ? getInvitationsByOrgId(orgId ?? DEF_JAM_ORG_ID)
    : apiInvites
  const roles = isDemo ? getCustomRolesByOrgId(orgId ?? DEF_JAM_ORG_ID) : customRoles

  const currentUserId = isDemo ? DEMO_CURRENT_USER_ID : profile?.userId

  const pendingInvites = useMemo(
    () => invites.filter((i) => (i.status ?? "pending") === "pending"),
    [invites]
  )

  // ── UI state ────────────────────────────────────────────────────────
  const [tab, setTab] = useState<Tab>("members")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [showInvite, setShowInvite] = useState(false)
  const [editMember, setEditMember] = useState<any | null>(null)

  const rows = tab === "members" ? members : pendingInvites
  const totalRows = rows.length
  const pageCount = Math.max(1, Math.ceil(totalRows / rowsPerPage))
  const currentPage = Math.min(page, pageCount - 1)
  const start = currentPage * rowsPerPage
  const pagedRows = rows.slice(start, start + rowsPerPage)
  const rangeStart = totalRows === 0 ? 0 : start + 1
  const rangeEnd = Math.min(start + rowsPerPage, totalRows)

  const switchTab = (next: Tab) => {
    setTab(next)
    setPage(0)
  }

  // ── Handlers ────────────────────────────────────────────────────────
  const handleInvite = async (
    email: string,
    roleId: string | undefined,
    customRoleId: string | undefined
  ) => {
    if (isDemo) {
      notifyDemoAction(`Invite ${email}`, true)
      return
    }
    await createInvite(email, roleId, customRoleId)
  }

  const handleRoleChange = async (member: any, value: string) => {
    if (value === member.role) return
    if (isDemo) {
      notifyDemoAction(`Change role to ${ROLE_LABELS[value] ?? value}`, true)
      return
    }
    await updateMember(member.userId, value, undefined)
  }

  const handleUpdateRole = async (
    userId: string,
    roleId: any,
    customRoleId: string | undefined
  ) => {
    if (isDemo) {
      notifyDemoAction("Update member role", true)
      setEditMember(null)
      return
    }
    await updateMember(userId, roleId, customRoleId)
    setEditMember(null)
  }

  const handleRemove = async (userId: string) => {
    if (isDemo) {
      notifyDemoAction("Remove member", true)
      setEditMember(null)
      return
    }
    await removeMember(userId)
    setEditMember(null)
  }

  const handleResend = async (invite: any) => {
    if (isDemo) {
      notifyDemoAction(`Resend invite to ${invite.invitedEmail}`, true)
      return
    }
    await resendInvite(invite.orgInviteId)
  }

  const handleCancelInvite = async (invite: any) => {
    if (isDemo) {
      notifyDemoAction(`Cancel invite to ${invite.invitedEmail}`, true)
      return
    }
    await cancelInvite(invite.orgInviteId)
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-4xl px-6 py-8 lg:px-8">
        <SettingsSectionHeader
          title="Members"
          description="Manage team members and invitations"
          action={
            <Button onClick={() => setShowInvite(true)}>
              <UserPlus className="size-4" />
              Invite Members
            </Button>
          }
        />

        {/* Tabs */}
        <div className="mt-6 flex items-center gap-6 border-b border-border">
          <TabButton
            active={tab === "members"}
            onClick={() => switchTab("members")}
            label="Team Members"
            count={members.length}
          />
          <TabButton
            active={tab === "invitations"}
            onClick={() => switchTab("invitations")}
            label="Pending Invitations"
            count={pendingInvites.length}
          />
        </div>

        {/* Table */}
        <div className="mt-2 overflow-hidden">
          {tab === "members" ? (
            <Table>
              <TableHeader className="sr-only">
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedRows.map((member) => {
                  const displayName =
                    member.userProfile?.displayName ||
                    member.displayName ||
                    "Unknown User"
                  const isSelf = member.userId === currentUserId
                  const isOwner = member.role === "primary_owner"
                  return (
                    <TableRow key={member.userId} className="hover:bg-muted/40">
                      <TableCell className="py-3">
                        <div className="flex items-center gap-3">
                          <Avatar
                            src={member.userProfile?.profileImageUrl}
                            alt={displayName}
                            fallback={getInitials(displayName)}
                            className="size-9"
                          />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-foreground">
                              {displayName}
                              {isSelf && (
                                <span className="text-muted-foreground"> (You)</span>
                              )}
                            </p>
                            <p className="truncate text-xs text-muted-foreground">
                              {member.orgEmail || member.userProfile?.email}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="w-44 py-3 text-right">
                        <div className="flex justify-end">
                          <Select
                            value={member.role}
                            onValueChange={(v: string | null) =>
                              v && handleRoleChange(member, v)
                            }
                            disabled={isOwner || isSelf}
                          >
                            <SelectTrigger size="sm" className="w-36">
                              <SelectValue>{roleDisplay(member)}</SelectValue>
                            </SelectTrigger>
                            <SelectPopup>
                              {member.role === "custom" && member.customRoleName && (
                                <SelectItem value="custom">
                                  {member.customRoleName}
                                </SelectItem>
                              )}
                              {STANDARD_ROLES.map((r) => (
                                <SelectItem key={r.value} value={r.value}>
                                  {r.label}
                                </SelectItem>
                              ))}
                            </SelectPopup>
                          </Select>
                        </div>
                      </TableCell>
                      <TableCell className="w-12 py-3 text-right">
                        <Menu>
                          <MenuTrigger
                            render={
                              <button
                                className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
                                disabled={isSelf}
                                aria-label={`Actions for ${displayName}`}
                              >
                                <MoreHorizontal className="size-4" />
                              </button>
                            }
                          />
                          <MenuPopup align="end" className="w-44">
                            <MenuItem onClick={() => setEditMember(member)}>
                              Edit member
                            </MenuItem>
                            <MenuItem
                              variant="destructive"
                              onClick={() => handleRemove(member.userId)}
                            >
                              Remove member
                            </MenuItem>
                          </MenuPopup>
                        </Menu>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          ) : (
            <Table>
              <TableHeader className="sr-only">
                <TableRow>
                  <TableHead>Invitee</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedRows.map((invite) => (
                  <TableRow key={invite.orgInviteId} className="hover:bg-muted/40">
                    <TableCell className="py-3">
                      <div className="flex items-center gap-3">
                        <Avatar
                          fallback={getInitials(invite.invitedEmail || "?")}
                          className="size-9"
                        />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-foreground">
                            {invite.invitedEmail}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            Invited by{" "}
                            {invite.invitedByProfile?.displayName ?? "team"}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-3 text-sm text-muted-foreground">
                      {invite.customRoleName ?? ROLE_LABELS[invite.role] ?? invite.role}
                    </TableCell>
                    <TableCell className="py-3">
                      <span className="inline-flex items-center rounded-full bg-amber-500/10 px-2.5 py-0.5 text-xs font-medium capitalize text-amber-500">
                        {invite.status ?? "pending"}
                      </span>
                    </TableCell>
                    <TableCell className="w-12 py-3 text-right">
                      <Menu>
                        <MenuTrigger
                          render={
                            <button
                              className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                              aria-label={`Actions for ${invite.invitedEmail}`}
                            >
                              <MoreHorizontal className="size-4" />
                            </button>
                          }
                        />
                        <MenuPopup align="end" className="w-44">
                          <MenuItem onClick={() => handleResend(invite)}>
                            Resend invite
                          </MenuItem>
                          <MenuItem
                            variant="destructive"
                            onClick={() => handleCancelInvite(invite)}
                          >
                            Cancel invite
                          </MenuItem>
                        </MenuPopup>
                      </Menu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {totalRows === 0 && (
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <div className="mb-3 flex size-12 items-center justify-center rounded-xl bg-muted">
                <Users className="size-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-foreground">
                {tab === "members" ? "No members yet" : "No pending invitations"}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {tab === "members"
                  ? "Invite team members to start collaborating."
                  : "Invitations you send will appear here."}
              </p>
            </div>
          )}
        </div>

        {/* Pagination footer */}
        {totalRows > 0 && (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Rows per page</span>
              <Select
                value={String(rowsPerPage)}
                onValueChange={(v: string | null) => {
                  if (!v) return
                  setRowsPerPage(Number(v))
                  setPage(0)
                }}
              >
                <SelectTrigger size="sm" className="w-18 min-w-0">
                  <SelectValue>{rowsPerPage}</SelectValue>
                </SelectTrigger>
                <SelectPopup>
                  {ROWS_PER_PAGE_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectPopup>
              </Select>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                {rangeStart}-{rangeEnd} of {totalRows}
              </span>
              <div className="flex items-center gap-1">
                <PageButton
                  onClick={() => setPage(0)}
                  disabled={currentPage === 0}
                  label="First page"
                >
                  <ChevronsLeft className="size-4" />
                </PageButton>
                <PageButton
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  label="Previous page"
                >
                  <ChevronLeft className="size-4" />
                </PageButton>
                <PageButton
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  disabled={currentPage >= pageCount - 1}
                  label="Next page"
                >
                  <ChevronRight className="size-4" />
                </PageButton>
                <PageButton
                  onClick={() => setPage(pageCount - 1)}
                  disabled={currentPage >= pageCount - 1}
                  label="Last page"
                >
                  <ChevronsRight className="size-4" />
                </PageButton>
              </div>
            </div>
          </div>
        )}
      </div>

      {showInvite && (
        <InviteMembersModal
          isDemo={isDemo}
          orgId={orgId ?? undefined}
          customRoles={roles as any}
          onClose={() => setShowInvite(false)}
          onSubmit={handleInvite}
        />
      )}
      {editMember && (
        <EditMemberModal
          isDemo={isDemo}
          orgId={orgId ?? undefined}
          member={editMember}
          customRoles={roles as any}
          onClose={() => setEditMember(null)}
          onUpdateRole={handleUpdateRole}
          onRemoveMember={handleRemove}
        />
      )}
    </div>
  )
}

function TabButton({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean
  onClick: () => void
  label: string
  count: number
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "-mb-px flex items-center gap-2 border-b-2 px-1 pb-3 text-sm font-medium transition-colors",
        active
          ? "border-foreground text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground"
      )}
    >
      {label}
      <span
        className={cn(
          "rounded-full px-1.5 py-0.5 text-xs font-medium",
          active ? "bg-muted text-foreground" : "bg-muted/60 text-muted-foreground"
        )}
      >
        {count}
      </span>
    </button>
  )
}

function PageButton({
  onClick,
  disabled,
  label,
  children,
}: {
  onClick: () => void
  disabled: boolean
  label: string
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="inline-flex size-8 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
    >
      {children}
    </button>
  )
}

export default MembersSection
